/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Tomas Burian (burian@cadwork.cz)
 *
 */

import {DataStorage, EEmbededElementTypes, RenderDocument} from "js_renderer";
import Element from "./element";
import Factory from "./factory";
import {vec3} from "gl-matrix";

export default
class SkyDome extends Element {

    public static TypeName: string = EEmbededElementTypes.SKYDOME;
    protected _url: string;
    private _parameters: { phiStart: number; heightSegments: number; thetaLength: number; phiLength: number; radius: number; widthSegments: number; thetaStart: number };

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);
    }

    public static create(aRenderDocument: RenderDocument): SkyDome {
        let element = new SkyDome(aRenderDocument);
        element._typeName = SkyDome.TypeName;
        return element;
    }

    protected _generateElementDescriptionJSON(): any {
        return {
            "type": this.getTypeName(),
            "id": this.getTypeName() + "_" + this._UID,   // this is is not important in the engine, it can identify e.g. id of an element in Lexocad
            "UID": this._UID
        };
    }

    public getFogColor(): number[] {
        return [0.843, 0.8, 0.69];
    }

    public set(aRadius: number, aWidthSegments: number, aHeightSegments: number, aPhiStart: number, aPhiLength: number, aThetaStart: number, aThetaLength: number) {
        this._parameters = {
            radius: aRadius,
            widthSegments: aWidthSegments,
            heightSegments: aHeightSegments,
            phiStart: aPhiStart,
            phiLength: aPhiLength,
            thetaStart: aThetaStart,
            thetaLength: aThetaLength
        };
    }

    private _createSphere(aRadius: number, aWidthSegments: number, aHeightSegments: number, aPhiStart: number, aPhiLength: number, aThetaStart: number, aThetaLength: number,
                          indices: number[], vertices: number[], normals: number[], uvs: number[]) {
        aRadius = aRadius || 50;

        aWidthSegments = Math.max(3, Math.floor(aWidthSegments) || 8);
        aHeightSegments = Math.max(2, Math.floor(aHeightSegments) || 6);

        aPhiStart = aPhiStart !== undefined ? aPhiStart : 0;
        aPhiLength = aPhiLength !== undefined ? aPhiLength : Math.PI * 2;

        aThetaStart = aThetaStart !== undefined ? aThetaStart : 0;
        aThetaLength = aThetaLength !== undefined ? aThetaLength : Math.PI;

        let thetaEnd = aThetaStart + aThetaLength;
        let ix;
        let iy;

        let index = 0;
        let grid = [];

        let vertex = vec3.create();
        let normal = vec3.create();

        // generate vertices, normals and uvs
        for (iy = 0; iy <= aHeightSegments; iy++) {

            let verticesRow = [];

            let v = iy / aHeightSegments;

            for (ix = 0; ix <= aWidthSegments; ix++) {
                let u = ix / aWidthSegments;

                // vertex
                vertex[0] = -aRadius * Math.cos(aPhiStart + u * aPhiLength) * Math.sin(aThetaStart + v * aThetaLength);
                vertex[1] = aRadius * Math.cos(aThetaStart + v * aThetaLength);
                vertex[2] = aRadius * Math.sin(aPhiStart + u * aPhiLength) * Math.sin(aThetaStart + v * aThetaLength);

                vertices.push(vertex[0], vertex[1], vertex[2]);

                // normal
                normal = vec3.fromValues(vertex[0], vertex[1], vertex[2]);
                vec3.normalize(normal, normal);
                normals.push(normal[0], normal[1], normal[2]);

                // uv
                uvs.push(u, 1 - v);

                verticesRow.push(index++);
            }
            grid.push(verticesRow);
        }

        // indices
        for (iy = 0; iy < aHeightSegments; iy++) {

            for (ix = 0; ix < aWidthSegments; ix++) {

                let a = grid[iy][ix + 1];
                let b = grid[iy][ix];
                let c = grid[iy + 1][ix];
                let d = grid[iy + 1][ix + 1];

                if (iy !== 0 || aThetaStart > 0) {
                    indices.push(a, b, d);
                }
                if (iy !== aHeightSegments - 1 || thetaEnd < Math.PI) {
                    indices.push(b, c, d);
                }
            }
        }
    }

    protected _generateGeometryObjectDescriptionJSON(): any {
        let indices = [];
        let vertices = [];
        let normals = [];
        let uvs = [];

        this._createSphere(this._parameters.radius, this._parameters.widthSegments, this._parameters.heightSegments,
            this._parameters.phiStart, this._parameters.phiLength, this._parameters.thetaStart, this._parameters.thetaLength,
            indices, vertices, normals, uvs);

        let appearanceContent: any = {};

        appearanceContent.material = {
            diffuseColor: [1, 1, 1],
            ambientColor: [0, 0, 1],
            emissiveColor: [1, 1, 1],
            specularColor: [1, 1, 1],
            shininess: 0.2,
            transparency: 0
        };

        return [{
            description: {
                type: "SKYDOME",
                bboxMin: [0.0, 0.0, 0.0],
                bboxMax: [100.0, 100.0, 100.0]
            },
            appearance: appearanceContent,
            indexedFaceSet: {
                src: this._renderDocument.getRenderer()._dataStorage.createLocalResourceUri(),
                vertexCount: vertices.length,
                normalCount: 0,
                texcoordCount: 0,
                indexCount: indices.length,
                binSize: 0,
                positions: vertices,
                normals: [],
                texCoords: [],
                indices: indices
            }
        }];
    }
}

Factory.register(SkyDome);
